import { createApi } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'

import type { TableParamsType } from 'common/interfaces/ITable'
import { UtilService } from 'common/services/utilService'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { type ApiPagination } from 'types'

export interface ITransactionMappingItem {
  id: number
  appointment_id: number
  patient_id: number
  amount: string
  transaction_id: string
  created_by: string
  created_at: string
  pat_fname: string
  pat_lname: string
  pat_phone: string
  appointment_with: string
}

export interface ITransactionMappingPaginated {
  items: ITransactionMappingItem[]
  total_items: number
}

export interface ITransactionWithAppointment {
  id: number
  appointment_id: number | null
  patient_id: number | null
  amount: string
  transaction_id: string
  created_by: string
  synced_with_emr: number
  pat_fname: string
  pat_lname: string
  pat_phone: string
  appointment_with: string
}

export type ITransactionWithKnownPatient = ITransactionWithAppointment

export interface ITransactionWithNewPatient extends ITransactionWithAppointment {
  dob: string
  gender: string
}

export interface ITransactionsMappingResponse {
  transactions_with_appointment: ITransactionWithAppointment[]
  transactions_with_known_patient: ITransactionWithKnownPatient[]
  transactions_with_new_patient: ITransactionWithNewPatient[]
}

export interface ITransactionHistoryItem {
  id: number
  appointment_id: number | null
  patient_id: number | null
  amount: string
  transaction_id: string
  created_by: string
  synced_with_emr: number
  pat_fname: string
  pat_lname: string
  pat_phone: string
  appointment_with: string
  created_at?: string
}

export type EncounterData = {
  eid: number
  euuid: string
  date: string
  date_formatted: string
  reason: string
  onset_date: string | null
  sensitivity: string
  billing_note: string | null
  pc_catid: number
  last_level_billed: number
  last_level_closed: number
  last_stmt_date: string | null
  stmt_count: number
  supervisor_id: number
  invoice_refno: string
  referral_source: string
  billing_facility: number
  external_id: string
  last_update: string
  pos_code: number
  class_code: string
  class_title: string
  pc_catname: string
  pid: number
  puuid: string
  facility_id: number
  facility_uuid: string
  facility_name: string
  facility_location_uuid: string | null
  billing_facility_id: number | null
  billing_facility_uuid: string | null
  billing_facility_name: string | null
  billing_location_uuid: string | null
  provider: {
    id: number
    full_name: string
  } | null
  provider_id: number
  referring_provider_id: number
  ordering_provider_id: number
  provider_uuid: string
  provider_username: string
  referrer_uuid: string | null
  referrer_username: string | null
  discharge_disposition: string | null
  discharge_disposition_text: string | null
}

export type EncounterDataResponse = {
  data: EncounterData[]
  links: { first: string }
  internalErrors: Array<any>
  validationErrors: Array<any>
}

const { withPaginationParams } = UtilService

export const transactionsMappingApi = createApi({
  reducerPath: 'transactionsMapping',
  baseQuery: baseQuery(),
  tagTypes: ['TransactionsMapping'],
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchTransactionsMapping: build.query<
      ITransactionsMappingResponse,
      { siteId: string; tableParams: TableParamsType }
    >({
      query: ({ siteId, tableParams }) => {
        const url = withPaginationParams('emr/appointments-transactions', tableParams)
        return {
          url,
          headers: { 'Database-Connection': siteId },
        }
      },
      providesTags: ['TransactionsMapping'],
    }),
    fetchTransactionsHistory: build.query<
      { items: ITransactionHistoryItem[]; pagination: ApiPagination; total_items: number },
      { siteId: string; tableParams?: TableParamsType }
    >({
      query: ({ siteId, tableParams }) => {
        const url = withPaginationParams('emr/appointments-transactions', tableParams, {
          history: true,
        })
        return {
          url,
          headers: { 'Database-Connection': siteId },
        }
      },
    }),
    fetchPatientEncounters: build.query<
      { encounters: EncounterDataResponse },
      { patientId: number }
    >({
      query: ({ patientId }) => ({
        url: `emr/get-patient-encounters/${patientId}`,
      }),
    }),
    resolveTransaction: build.mutation<
      any,
      { id: number; patient_id: number; encounter_id: number; siteId: string }
    >({
      query: ({ id, patient_id, encounter_id, siteId }) => ({
        url: 'emr/appointments-transactions',
        method: 'POST',
        data: { id, patient_id, encounter_id },
        headers: { 'Database-Connection': siteId },
      }),
      invalidatesTags: ['TransactionsMapping'],
    }),
    overwriteEncounter: build.mutation<
      any,
      { id: number; encounter_id: number; encounter_uuid: string; siteId: string }
    >({
      query: ({ id, encounter_id, encounter_uuid, siteId }) => ({
        url: 'emr/appointments-transactions/overwrite-encounter',
        method: 'POST',
        data: { id, encounter_id, encounter_uuid },
        headers: { 'Database-Connection': siteId },
      }),
      invalidatesTags: ['TransactionsMapping'],
    }),
    overwriteAppointmentEncounter: build.mutation<
      any,
      { appointment_id: number; encounter_id: number; encounter_uuid: string; siteId: string }
    >({
      query: ({ appointment_id, encounter_id, encounter_uuid, siteId }) => ({
        url: 'emr/appointments-transactions/overwrite-encounter',
        method: 'POST',
        data: { appointment_id, encounter_id, encounter_uuid },
        headers: { 'Database-Connection': siteId },
      }),
    }),
  }),
})

export const {
  useLazyFetchTransactionsMappingQuery,
  useLazyFetchTransactionsHistoryQuery,
  useLazyFetchPatientEncountersQuery,
  useFetchPatientEncountersQuery,
  useResolveTransactionMutation,
  useOverwriteEncounterMutation,
  useOverwriteAppointmentEncounterMutation,
} = transactionsMappingApi
